import { appConfig } from "@/core/config/env";

/**
 * Foundation HTTP client. No endpoints are defined here — feature services
 * will compose this client once the RMS architecture document defines the
 * API surface.
 *
 * Design decisions:
 *  - Authentication is injected via `setAuthTokenProvider`, keeping token
 *    storage out of this module (no token leakage into logs or globals).
 *  - A 401 on a protected call triggers a single silent refresh via the
 *    `sessionRefresher` registered by the auth module. If refresh succeeds
 *    the original request is retried with the new access token; if it
 *    fails the SPA is bounced to /login. Concurrent 401s share the same
 *    in-flight refresh promise so we don't spam /auth/refresh.
 *  - Errors are normalised to `ApiError` so every feature handles failures
 *    the same way.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly body: unknown,
  ) {
    super(`API request failed: ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

/**
 * Shown when the server refuses a call the caller holds no grant for. The
 * PermissionsGuard answers with the terse "Insufficient permissions", which
 * reads as an internal code to shop staff, so that one message is replaced;
 * the OwnershipGuard's 403 already explains itself in plain words and is
 * passed through untouched.
 */
export const NO_PERMISSION_MESSAGE =
  "You do not have permission to do this. Contact an administrator if you need access.";

/** The backend's generic permission refusal, replaced by the sentence above. */
const GENERIC_FORBIDDEN_MESSAGE = "Insufficient permissions";

/**
 * The backend's error message for a failed call, or `fallback` when the
 * failure carries none (network error, unexpected shape). Every feature
 * surfaces API failures through this so the operator sees the server's
 * explanation rather than a generic string.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const body = error.body as { error?: { message?: string } } | undefined;
    const message = body?.error?.message;

    if (error.status === 403 && (!message || message === GENERIC_FORBIDDEN_MESSAGE)) {
      return NO_PERMISSION_MESSAGE;
    }

    return message ?? fallback;
  }
  return fallback;
}

type AuthTokenProvider = () => string | undefined;
type SessionInvalidator = () => void;
/** Resolves once the auth module has completed a silent refresh (or throws on failure). */
type SessionRefresher = () => Promise<void>;

let authTokenProvider: AuthTokenProvider = () => undefined;
let sessionInvalidator: SessionInvalidator = () => undefined;
let sessionRefresher: SessionRefresher | null = null;

/** Registered once by the (future) auth module. */
export function setAuthTokenProvider(provider: AuthTokenProvider): void {
  authTokenProvider = provider;
}

/**
 * Registered by the auth module so the 401 interceptor can wipe the
 * persisted session before bouncing to /login. Without this, the login
 * page would see `isAuthenticated=true` (localStorage still populated)
 * and immediately redirect back to the protected route — an infinite
 * loop.
 */
export function setSessionInvalidator(invalidator: SessionInvalidator): void {
  sessionInvalidator = invalidator;
}

/**
 * Registered by the auth module. Called on 401 to attempt a silent
 * `/auth/refresh` — must throw if the refresh fails. When null, 401s
 * fall straight through to the /login redirect (legacy behaviour).
 */
export function setSessionRefresher(refresher: SessionRefresher | null): void {
  sessionRefresher = refresher;
}

export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Suppress the global 401 handling for this call (used by the auth
   * endpoints themselves — login/refresh must not attempt to refresh
   * on their own 401s).
   */
  skipAuthRedirect?: boolean;
}

/** Shared in-flight refresh so concurrent 401s only hit /auth/refresh once. */
let refreshInFlight: Promise<void> | null = null;

function runSilentRefresh(): Promise<void> {
  if (!sessionRefresher) {
    return Promise.reject(new Error("No session refresher registered"));
  }
  if (!refreshInFlight) {
    refreshInFlight = sessionRefresher().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function redirectToLogin(): void {
  sessionInvalidator();
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?reason=expired&next=${next}`);
}

async function executeFetch(
  method: string,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };

  // A caller-supplied Authorization header wins (the SSO exchange presents the
  // IRWMS token, not the WMS one).
  const token = authTokenProvider();
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method,
    headers,
    signal: options.signal,
    // Send the HttpOnly refresh cookie on every request. Required for
    // /auth/refresh; harmless on other endpoints.
    credentials: "include",
  };

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  return fetch(`${appConfig.apiBaseUrl}${path}`, init);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  let response = await executeFetch(method, path, body, options);

  // Silent refresh + single retry on 401 for protected calls.
  if (response.status === 401 && !options.skipAuthRedirect && sessionRefresher) {
    try {
      await runSilentRefresh();
      response = await executeFetch(method, path, body, options);
    } catch {
      // Refresh itself failed — bounce to login below.
    }
  }

  if (!response.ok) {
    const errorBody: unknown = await response.json().catch(() => undefined);

    if (response.status === 401 && !options.skipAuthRedirect) {
      redirectToLogin();
    }

    throw new ApiError(response.status, response.statusText, errorBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const json: unknown = await response.json();

  // The WMS backend wraps every success in an envelope: `{ success: true,
  // data: T }` (TransformResponseInterceptor). Unwrap it so callers receive
  // the payload directly. Non-enveloped responses pass through unchanged.
  if (
    json !== null &&
    typeof json === "object" &&
    "success" in json &&
    (json as { success: unknown }).success === true &&
    "data" in json
  ) {
    return (json as { data: T }).data;
  }

  return json as T;
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>("DELETE", path, undefined, options),
};
